import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as lotesApi from '../../api/lotes';
import * as fornecedoresApi from '../../api/fornecedores';
import * as produtosApi from '../../api/produtos';
import * as qualidadesApi from '../../api/qualidades';
import * as unidadesApi from '../../api/unidadesMedida';
import { useExigeEmpresaEspecifica } from '../../hooks/useExigeEmpresaEspecifica';
import AvisoSelecioneEmpresa from '../../components/common/AvisoSelecioneEmpresa';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import SearchableSelect from '../../components/common/SearchableSelect';
import { todayIso } from '../../utils/date';
import { getErrorMessage } from '../../utils/apiError';

const ITEM_VAZIO = { id_qualidade: '', id_unidade_medida: '', quantidade_recebida: '' };

export default function LoteNovo() {
  const navigate = useNavigate();
  const { bloqueado, carregandoEmpresas, semEmpresaCadastrada } = useExigeEmpresaEspecifica();
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  const [fornecedores, setFornecedores] = useState([]);
  const [produtos, setProdutos] = useState([]);
  const [qualidades, setQualidades] = useState([]);
  const [unidades, setUnidades] = useState([]);

  const [codigo, setCodigo] = useState('');
  const [idFornecedor, setIdFornecedor] = useState('');
  const [idProduto, setIdProduto] = useState('');
  const [dataEntrada, setDataEntrada] = useState(todayIso());
  const [observacoes, setObservacoes] = useState('');
  const [itens, setItens] = useState([{ ...ITEM_VAZIO }]);

  useEffect(() => {
    if (bloqueado) {
      setLoading(false);
      return;
    }
    Promise.all([fornecedoresApi.listFornecedores(), produtosApi.listProdutos(), qualidadesApi.listQualidades(), unidadesApi.listUnidades()])
      .then(([f, p, q, u]) => {
        setFornecedores(f.data);
        setProdutos(p.data);
        setQualidades(q.data);
        setUnidades(u.data);
        const unidadeSaco = u.data.find((un) => un.nome.toLowerCase() === 'saco');
        if (unidadeSaco) setItens([{ ...ITEM_VAZIO, id_unidade_medida: unidadeSaco.id_unidade_medida }]);
      })
      .catch(() => setErro('Erro ao carregar dados de apoio.'))
      .finally(() => setLoading(false));
  }, [bloqueado]);

  const atualizarItem = (index, campo, valor) => {
    setItens((atual) => atual.map((it, i) => (i === index ? { ...it, [campo]: valor } : it)));
  };

  const adicionarItem = () => {
    const unidadePadrao = itens[itens.length - 1]?.id_unidade_medida || '';
    setItens((atual) => [...atual, { ...ITEM_VAZIO, id_unidade_medida: unidadePadrao }]);
  };

  const removerItem = (index) => setItens((atual) => atual.filter((_, i) => i !== index));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErro('');
    if (!idFornecedor || !idProduto) {
      setErro('Selecione o fornecedor e o produto.');
      return;
    }
    if (itens.some((it) => !it.id_qualidade || !it.id_unidade_medida || !it.quantidade_recebida)) {
      setErro('Preencha qualidade, unidade e quantidade em todos os itens.');
      return;
    }
    setSalvando(true);
    try {
      const { data } = await lotesApi.createLote({
        codigo,
        id_fornecedor: idFornecedor,
        id_produto: idProduto,
        data_entrada: dataEntrada,
        observacoes: observacoes || null,
        itens,
      });
      navigate(`/lotes/${data.id_lote}`);
    } catch (err) {
      setErro(getErrorMessage(err, 'Erro ao registrar entrada.'));
    } finally {
      setSalvando(false);
    }
  };

  if (carregandoEmpresas || loading) return <LoadingSpinner />;

  if (bloqueado) {
    return (
      <div>
        <PageHeader titulo="Nova entrada (Lote)" />
        <AvisoSelecioneEmpresa semEmpresaCadastrada={semEmpresaCadastrada} />
      </div>
    );
  }

  return (
    <div>
      <PageHeader titulo="Nova entrada (Lote)" subtitulo="Registre o recebimento de um fornecedor, com a quantidade de cada qualidade" />
      <form onSubmit={handleSubmit} className="sf-card p-4">
        {erro && (
          <div className="sf-login-alert mb-3">
            <i className="bi bi-exclamation-circle" />
            {erro}
          </div>
        )}

        <div className="row g-3 mb-2">
          <div className="col-md-4">
            <label className="sf-field-label">
              <span>Código/chave do lote</span>
              <span className="sf-field">
                <i className="bi bi-upc-scan" />
                <input required autoFocus value={codigo} onChange={(e) => setCodigo(e.target.value)} placeholder="Ex: LOTE-0032" />
              </span>
            </label>
          </div>
          <div className="col-md-4">
            <label className="sf-field-label">
              <span>Fornecedor</span>
              <SearchableSelect
                options={fornecedores.map((f) => ({ value: f.id_fornecedor, label: f.nome }))}
                value={idFornecedor}
                onChange={setIdFornecedor}
                placeholder="Selecione o fornecedor..."
                required
              />
            </label>
          </div>
          <div className="col-md-4">
            <label className="sf-field-label">
              <span>Data de entrada</span>
              <span className="sf-field">
                <i className="bi bi-calendar3" />
                <input type="date" required value={dataEntrada} onChange={(e) => setDataEntrada(e.target.value)} />
              </span>
            </label>
          </div>
        </div>

        <label className="sf-field-label mb-3">
          <span>Produto</span>
          <SearchableSelect
            options={produtos.map((p) => ({ value: p.id_produto, label: p.nome }))}
            value={idProduto}
            onChange={setIdProduto}
            placeholder="Selecione o produto..."
            required
          />
        </label>

        <div className="sf-nav-section-title"><span>Quantidade recebida por qualidade</span></div>

        {itens.map((item, index) => (
          <div className="row g-2 align-items-end mb-2" key={index}>
            <div className="col-5 col-md-4">
              <label className="sf-field-label">
                <span>Qualidade</span>
                <SearchableSelect
                  options={qualidades.map((q) => ({ value: q.id_qualidade, label: q.nome }))}
                  value={item.id_qualidade}
                  onChange={(v) => atualizarItem(index, 'id_qualidade', v)}
                  placeholder="Qualidade..."
                  required
                />
              </label>
            </div>
            <div className="col-4 col-md-3">
              <label className="sf-field-label">
                <span>Quantidade</span>
                <span className="sf-field">
                  <input
                    type="number"
                    min="0.001"
                    step="0.001"
                    required
                    value={item.quantidade_recebida}
                    onChange={(e) => atualizarItem(index, 'quantidade_recebida', e.target.value)}
                  />
                </span>
              </label>
            </div>
            <div className="col-4 col-md-3">
              <label className="sf-field-label">
                <span>Unidade</span>
                <SearchableSelect
                  options={unidades.map((u) => ({ value: u.id_unidade_medida, label: `${u.nome} (${u.sigla})` }))}
                  value={item.id_unidade_medida}
                  onChange={(v) => atualizarItem(index, 'id_unidade_medida', v)}
                  placeholder="Unidade..."
                  required
                />
              </label>
            </div>
            <div className="col-2 col-md-2 d-flex">
              {itens.length > 1 && (
                <button
                  type="button"
                  className="sf-btn sf-btn-ghost"
                  style={{ color: 'var(--sf-danger-fg)', borderColor: '#f0cfcc' }}
                  onClick={() => removerItem(index)}
                  title="Remover"
                >
                  <i className="bi bi-trash" />
                </button>
              )}
            </div>
          </div>
        ))}
        <button type="button" className="sf-btn sf-btn-ghost mt-1" onClick={adicionarItem}>
          <i className="bi bi-plus-lg" />
          Adicionar qualidade
        </button>

        <div className="mt-4 mb-3">
          <label className="sf-field-label">
            <span>Observações</span>
            <textarea className="form-control" rows={2} value={observacoes} onChange={(e) => setObservacoes(e.target.value)} />
          </label>
        </div>

        <button type="submit" className="sf-btn sf-btn-primary" disabled={salvando}>
          {salvando ? 'Salvando...' : 'Registrar entrada'}
        </button>
      </form>
    </div>
  );
}
